import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSession } from '../lib/sessionContext'
import { documentTitle, rememberAppName, useRememberedAppName } from './pageTitles'

/**
 * Keeps the browser tab named after the screen it is showing.
 *
 * Rendered ONCE, above `<Routes>` — not per page. A hook called from sixty
 * screens is sixty chances for a new screen to forget it, and half of those
 * screens render more than one `PageHeader` (per tab, per state), so a title
 * set from a header would flip between two names on the same route. One
 * subscriber to `useLocation` cannot do either.
 *
 * It sits INSIDE `<LegacyRedirects>`, so a legacy URL is titled by the React
 * route it lands on rather than by the path the bookmark carried.
 *
 * Renders nothing.
 */
export default function DocumentTitle() {
  const { pathname } = useLocation()
  /* The session supplies the installation's own name for itself, and it arrives
     after first paint — so this reads it from context rather than caching it,
     and the title corrects itself the moment the bootstrap lands. */
  const { session } = useSession()
  /* Signed out there is no session; the name remembered from the login payload stands in,
     and re-titles the tab when it arrives. */
  const remembered = useRememberedAppName()

  /* A signed-in session carries the current name — keep the remembered copy fresh with it,
     so the sign-in page shows a rename after the next visit to the panel. */
  useEffect(() => {
    const settings = session?.settings
    if (settings) rememberAppName(settings.Default_ArabicApplicationTitle, settings.Default_EnglishApplicationTitle)
  }, [session])

  useEffect(() => {
    document.title = documentTitle(pathname, session)
  }, [pathname, session, remembered])

  return null
}
